import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { PhoneCall, Check, RefreshCw, BookOpen, ChevronDown, ChevronUp, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGER_ROLES = ['master_admin', 'domain_admin', 'domain_supervisor', 'manager', 'admin', 'office_manager'];

const fmtTime = (iso) => {
  try { return new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Brisbane' }); }
  catch { return ''; }
};

const PlaybookView = ({ row }) => {
  if (row.playbook_type === 'playbook' && row.playbook) {
    const pb = row.playbook;
    return (
      <div className="space-y-1.5 text-xs text-slate-700" data-testid={`morning-playbook-${row.id}`}>
        {pb.opening_line && <p><span className="font-bold text-teal-700">Opening:</span> “{pb.opening_line}”</p>}
        {(pb.points_to_cover || []).length > 0 && (
          <div><span className="font-bold text-teal-700">Cover:</span>
            <ul className="list-disc ml-4">{pb.points_to_cover.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
        )}
        {(pb.checklist || []).length > 0 && (
          <div><span className="font-bold text-teal-700">Checklist:</span>
            <ul className="list-disc ml-4">{pb.checklist.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </div>
        )}
        {pb.urgency_note && <p className="text-amber-700">{pb.urgency_note}</p>}
        {pb.pre_call_sms && <p><span className="font-bold text-teal-700">Pre-call SMS idea:</span> “{pb.pre_call_sms}”</p>}
      </div>
    );
  }
  const c = row.context || {};
  return (
    <div className="space-y-1 text-xs text-slate-700" data-testid={`morning-context-${row.id}`}>
      <p><span className="font-bold text-slate-500">Source:</span> {c.source || '—'} · <span className="font-bold text-slate-500">Status:</span> {c.status || '—'}{c.do_not_call && <span className="text-red-600 font-bold ml-2">DO NOT CALL flag!</span>}</p>
      {(c.notes || []).length > 0 && (
        <div><span className="font-bold text-slate-500">Latest notes:</span>
          {c.notes.map((n, i) => <p key={i} className="ml-2">• {n.by ? `${n.by}: ` : ''}“{n.body}”</p>)}
        </div>
      )}
      {(c.recent_activity || []).length > 0 && (
        <div><span className="font-bold text-slate-500">Recent activity:</span>
          {c.recent_activity.slice(0, 4).map((a, i) => <p key={i} className="ml-2">• {a.kind}{a.outcome ? ` (${a.outcome})` : ''} — {a.subject}{a.body ? `: ${a.body.slice(0, 90)}` : ''}</p>)}
        </div>
      )}
      {row.vf_last_call && <p className="text-teal-700">VoiceFabric: last {row.vf_last_call.direction || ''} call by {row.vf_last_call.agent_name || '—'}</p>}
    </div>
  );
};

export const MorningCallsCard = () => {
  const { user } = useAuth();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/tenant/gymsales/morning-calls`).then((r) => setData(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/tenant/gymsales/morning-calls/run`);
      if (r.data.error) toast.error(`Sweep hit an error: ${r.data.error}`);
      else toast.success(`Sweep done — ${r.data.found} call${r.data.found === 1 ? '' : 's'} due today (scanned ${r.data.scanned} leads)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Sweep failed'); }
    setRunning(false);
  };

  const markDone = async (id) => {
    setBusy(id);
    try {
      const r = await axios.post(`${API}/tenant/gymsales/morning-calls/${id}/done`);
      toast.success(r.data.gymsales_note?.ok === false ? 'Marked done (GymSales note failed — will show in logs)' : 'Done — note written back to GymSales');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(null);
  };

  if (!data) return null;
  const rows = data.rows || [];
  const pending = rows.filter((r) => !r.done);
  const sweepError = data.last_sweep?.error;
  if (rows.length === 0 && !isManager) return null;

  return (
    <div className="rounded-xl border border-sky-300 bg-sky-50 p-4 mb-4" data-testid="morning-calls-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <p className="font-semibold text-slate-900 flex items-center gap-2">
          <PhoneCall className="w-5 h-5 text-sky-600" />
          Today&apos;s GymSales calls
          {pending.length > 0 && <span className="text-xs font-bold bg-sky-500 text-white rounded-full px-2 py-0.5" data-testid="morning-calls-count">{pending.length}</span>}
          <span className="text-[11px] font-normal text-slate-400">{data.date} · swept 6:00am Brisbane</span>
        </p>
        {isManager && (
          <button onClick={runNow} disabled={running}
            className="text-xs font-semibold px-3 py-1.5 rounded-md bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white inline-flex items-center gap-1"
            data-testid="morning-calls-run">
            <RefreshCw className={`w-3.5 h-3.5 ${running ? 'animate-spin' : ''}`} /> {running ? 'Sweeping…' : 'Run sweep now'}
          </button>
        )}
      </div>
      {sweepError && isManager && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2" data-testid="morning-calls-error">
          Last sweep failed: {sweepError}{sweepError.includes('ROUTE_NOT_ALLOWED') ? ' — allowlist GET /people/ on your gs-router to enable the sweep.' : ''}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 bg-white border border-sky-100 rounded-lg px-3 py-2.5" data-testid="morning-calls-empty">
          No GymSales calls found for today{data.last_sweep ? '' : ' — the sweep hasn\u2019t run yet (6:00am daily, or hit "Run sweep now")'}.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div key={r.id} className={`bg-white border rounded-lg px-3 py-2 ${r.done ? 'border-slate-100 opacity-60' : 'border-sky-200'}`} data-testid={`morning-call-row-${r.person_id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[220px]">
                  <p className="text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{r.name}</span>
                    <span className="text-xs text-slate-400">{r.phone}</span>
                    <span className="text-[11px] font-bold text-sky-700 bg-sky-100 rounded-full px-2 py-0.5">{fmtTime(r.due_at)} · {r.subject}</span>
                    {r.vf_call_id && <span className="text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-1.5 py-0.5" title="Matched to a VoiceFabric call"><Zap className="w-2.5 h-2.5 inline" /> VF match</span>}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Assigned: <span className="font-semibold text-slate-700">{r.assigned_agent || 'Unassigned'}</span>
                    {r.source && <> · {r.source}</>}{r.status && <> · {r.status}</>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-white inline-flex items-center gap-1"
                    data-testid={`morning-call-playbook-${r.person_id}`}>
                    <BookOpen className="w-3.5 h-3.5" /> Playbook {openId === r.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {!r.done && (
                    <button onClick={() => markDone(r.id)} disabled={busy === r.id}
                      className="text-xs font-semibold px-2.5 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white inline-flex items-center gap-1"
                      data-testid={`morning-call-done-${r.person_id}`}>
                      <Check className="w-3.5 h-3.5" /> Done
                    </button>
                  )}
                  {r.done && <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">✓ {r.done_by ? r.done_by.split('@')[0] : 'done'}</span>}
                </div>
              </div>
              {openId === r.id && (
                <div className="mt-2 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                  <PlaybookView row={r} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
